import { zodResolver } from "@hookform/resolvers/zod";
import { format, type Locale } from "date-fns";
import { arSA } from "date-fns/locale";
import { CalendarIcon, Loader2 } from "lucide-react";
import { type PropsWithChildren, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { useI18nContext } from "~/i18n/i18n-react";
import useAuthStore from "~/stores/auth";
import { api } from "~/utils/api";
import { cn } from "~/utils/cn";
import { supportedLanguages } from "~/utils/supported-languages"; // adjust path as needed
import useCalendarLocale from "~/utils/use-calendar-locale";
import { TRACK_EVENTS } from "../app-insights/event-tracking/types";
import useTrackEvent from "../app-insights/event-tracking/useTrackEvent";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { TimePicker12Demo } from "../ui/time-picker-demo";

const createRoomSchema = z
  .object({
    name: z
      .string({ required_error: "Room name is required." })
      .min(5, { message: "Room name should be at least 5 characters." })
      .max(50, { message: "Room name cannot exceed 50 characters." }),
    language: z.enum([...supportedLanguages, "auto"]),
    startTime: z.date({ required_error: "Start time is required." }),
    endTime: z.date({ required_error: "End time is required." }),
  })
  .refine((data) => data.endTime > data.startTime, {
    message: "End time must be after the start time.",
    path: ["endTime"],
  });

type CreateFormInputs = z.infer<typeof createRoomSchema>;

const NU_LOCALE = "ar-u-nu-arab";
const formatDay = (day: Date): string =>
  day.getDate().toLocaleString(NU_LOCALE);
const formatWeekNumber = (weekNumber: number): string =>
  weekNumber.toLocaleString(NU_LOCALE);
const formatMonthCaption = (date: Date, options?: { locale?: Locale }) => {
  const y = date.getFullYear().toLocaleString(NU_LOCALE);
  const m = format(date, "LLLL", options);
  return `${m} ${y}`;
};

export const CreateRoomModal = ({ children }: PropsWithChildren) => {
  const user = useAuthStore((state) => state.user);
  const trackCreateRoom = useTrackEvent({
    eventName: TRACK_EVENTS.CREATE_ROOM,
  });
  const { LL } = useI18nContext();
  const calendarLocale = useCalendarLocale();

  const [open, setOpen] = useState(false);
  const { mutate, isPending } = api.room.create.useMutation();
  const { room } = api.useUtils();

  const form = useForm<CreateFormInputs>({
    resolver: zodResolver(createRoomSchema),
    mode: "onChange",
  });

  const handleSubmit = (data: CreateFormInputs) => {
    const submitData = {
      language: data.language === "auto" ? undefined : data.language,
      userId: user!.id,
      name: data.name.trim(),
      startTime: data.startTime,
      endTime: data.endTime,
    };

    trackCreateRoom({ status: "creating", ...submitData });

    mutate(submitData, {
      onSettled: (data, error) => {
        if (error) {
          trackCreateRoom({
            status: "error",
            error: error.message,
            ...submitData,
          });
          toast.error(LL.SOMETHING_WENT_WRONG(), {
            description: error.message,
          });
        } else if (data) {
          trackCreateRoom({ status: "created", ...data, ...submitData });
          toast(LL.CREATE_ROOM_TOAST.TITLE(), {
            description: LL.CREATE_ROOM_TOAST.DESCRIPTION(),
          });
          void room.invalidate();
          form.reset();
          setOpen(false);
        }
      },
    });
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="overflow-auto" dir="auto">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <DialogHeader>
              <DialogTitle>{LL.CREATE_ROOM()}</DialogTitle>
              <DialogDescription>
                {LL.CREATE_ROOM_FORM.DESCRIPTION()}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {LL.CREATE_ROOM_FORM.NAME_LABEL()}
                        {"*"}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={LL.CREATE_ROOM_FORM.NAME_PLACEHOLDER()}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="language"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {LL.CREATE_ROOM_FORM.LANGUAGE()}
                        {"*"}
                      </FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} {...field}>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={LL.CREATE_ROOM_FORM.LANGUAGE_PLACEHOLDER()}
                            />
                          </SelectTrigger>
                          <SelectContent className="max-h-60 overflow-y-auto">
                            <SelectItem value="auto">
                              {LL.CREATE_ROOM_FORM.AUTO_DETECT()}
                            </SelectItem>
                            <SelectItem value="en-US">
                              {LL.CREATE_ROOM_FORM.ENGLISH_US()}
                            </SelectItem>
                            <SelectItem value="ar-SA">
                              {LL.CREATE_ROOM_FORM.ARABIC_KSA()}
                            </SelectItem>
                            <SelectItem value="es-CR">
                              {LL.CREATE_ROOM_FORM.SPANISH_CR()}
                            </SelectItem>
                            <SelectItem value="ca-ES">
                              {LL.CREATE_ROOM_FORM.CATALAN_ES()}
                            </SelectItem>
                            <SelectItem value="pt-PT">
                              {LL.CREATE_ROOM_FORM.PORTUGUESE_PT()}
                            </SelectItem>
                            <SelectItem value="pt-BR">
                              {LL.CREATE_ROOM_FORM.PORTUGUESE_BR()}
                            </SelectItem>
                            <SelectItem value="et-EE">
                              {LL.CREATE_ROOM_FORM.ESTONIAN_EE()}
                            </SelectItem>
                            <SelectItem value="ru-RU">
                              {LL.CREATE_ROOM_FORM.RUSSIAN_RU()}
                            </SelectItem>
                            <SelectItem value="mt-MT">
                              {LL.CREATE_ROOM_FORM.MALTESE_MT()}
                            </SelectItem>
                            <SelectItem value="fil-PH">
                              {LL.CREATE_ROOM_FORM.FILIPINO_PH()}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormDescription>
                        {LL.CREATE_ROOM_FORM.LANGUAGE_DESCRIPTION()}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-2">
                  <FormField
                    control={form.control}
                    name="startTime"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>
                          {LL.START_DATE_TIME()}
                          {"*"}
                        </FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "justify-start text-start font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="me-2 h-4 w-4" />
                              {field.value ? (
                                format(field.value, "PP h:mm a")
                              ) : (
                                <span>{LL.CREATE_ROOM_FORM.PICKDATE()}</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              locale={calendarLocale}
                              dir={calendarLocale === arSA ? "rtl" : "ltr"}
                              formatters={
                                calendarLocale === arSA
                                  ? {
                                      formatDay,
                                      formatMonthCaption,
                                      formatWeekNumber,
                                    }
                                  : undefined
                              }
                              mode="single"
                              selected={field.value}
                              onSelect={(event) => {
                                field.onChange(event);
                                if (form.getValues().endTime)
                                  void form.trigger("endTime");
                              }}
                              fromDate={new Date()}
                              initialFocus
                            />
                            <div className="border-t border-border p-3">
                              <TimePicker12Demo
                                setDate={(event) => {
                                  field.onChange(event);
                                  if (form.getValues().endTime)
                                    void form.trigger("endTime");
                                }}
                                date={field.value}
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endTime"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>
                          {LL.END_DATE_TIME()}
                          {"*"}
                        </FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "justify-start text-start font-normal",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <CalendarIcon className="me-2 h-4 w-4" />
                              {field.value ? (
                                format(field.value, "PP h:mm a")
                              ) : (
                                <span>{LL.CREATE_ROOM_FORM.PICKDATE()}</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              locale={calendarLocale}
                              dir={calendarLocale === arSA ? "rtl" : "ltr"}
                              formatters={
                                calendarLocale === arSA
                                  ? {
                                      formatDay,
                                      formatMonthCaption,
                                      formatWeekNumber,
                                    }
                                  : undefined
                              }
                              mode="single"
                              selected={field.value}
                              onSelect={(event) => {
                                field.onChange(event);
                                if (form.getValues().endTime)
                                  void form.trigger("endTime");
                              }}
                              fromDate={
                                new Date(
                                  form.getValues().startTime?.getTime() ??
                                    Date.now(),
                                )
                              }
                              initialFocus
                            />
                            <div className="border-t border-border p-3">
                              <TimePicker12Demo
                                setDate={(event) => {
                                  field.onChange(event);
                                  if (form.getValues().endTime)
                                    void form.trigger("endTime");
                                }}
                                date={field.value}
                              />
                            </div>
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={isPending || !form.formState.isValid}
              >
                {isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
                {LL.CREATE_ROOM()}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
